// src/services/stripeService.js
import { loadStripe } from '@stripe/stripe-js';
import { functions } from '../firebase';
import { httpsCallable } from 'firebase/functions';
import { PLAN_PRICES } from '../config/plans';

// Initialize Stripe only when publishable key is set (avoids "Cannot read properties of undefined (reading 'match')" in dev)
const stripeKey = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY;
const stripePromise =
  stripeKey && typeof stripeKey === "string" && stripeKey.trim().length > 0
    ? loadStripe(stripeKey.trim())
    : Promise.resolve(null);

// Firebase Functions references
const createPaymentIntentFn = httpsCallable(functions, 'createPaymentIntent');
const createSubscriptionFn = httpsCallable(functions, 'createSubscription');
const verifyPaymentStatusFn = httpsCallable(functions, 'verifyPaymentStatus');
const validateDiscountCodeFn = httpsCallable(functions, 'validateDiscountCode');
const getPaymentMethodsFn = httpsCallable(functions, 'getPaymentMethods');
const createSetupIntentFn = httpsCallable(functions, 'createSetupIntent');
const setDefaultPaymentMethodFn = httpsCallable(functions, 'setDefaultPaymentMethod');
const deletePaymentMethodFn = httpsCallable(functions, 'deletePaymentMethod');
const getPaymentMethodFromIntentFn = httpsCallable(functions, 'getPaymentMethodFromIntent');

/**
 * Stripe Service
 */
class StripeService {
  /**
   * Get Stripe instance
   */
  async getStripe() {
    if (!import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY) {
      throw new Error('Stripe publishable key is not configured');
    }
    return await stripePromise;
  }

  /**
   * Validate a discount code
   * @param {string} code - Discount code to validate
   * @returns {Promise<Object>} Validation result with discount details
   */
  async validateDiscountCode(code) {
    try {
      if (!code || typeof code !== 'string' || code.trim().length === 0) {
        return {
          valid: false,
          error: 'Please enter a discount code',
        };
      }

      const result = await validateDiscountCodeFn({ code: code.trim() });
      return result.data;
    } catch (error) {
      console.error('Error validating discount code:', error);
      return {
        valid: false,
        error: error.message || 'Failed to validate discount code',
      };
    }
  }

  /**
   * Create a payment intent.
   *
   * Note: no `amount` is sent. The price is resolved server-side from the
   * planId — see PLAN_PRICES in functions/src/index.ts. Sending an amount
   * would let anyone choose their own price by editing the checkout URL.
   *
   * @param {Object} params - Payment parameters
   * @param {string} params.planId - Plan ID (e.g., 'monthly', 'quarterly')
   * @param {string} params.planName - Plan name for display
   * @param {string} params.userId - User ID
   * @param {string} params.currency - Currency code (default: 'eur')
   * @param {string} params.discountCode - Optional discount code
   * @returns {Promise<Object>} Payment intent with client secret
   */
  async createPaymentIntent({ planId, planName, userId, currency = 'eur', discountCode }) {
    try {
      const result = await createPaymentIntentFn({
        planId,
        planName,
        userId,
        currency,
        discountCode: discountCode || null,
      });

      // Handle both direct data and nested data structures
      const responseData = result.data || result;

      return {
        success: true,
        clientSecret: responseData.clientSecret,
        paymentIntentId: responseData.paymentIntentId,
        amount: responseData.amount,
        originalAmount: responseData.originalAmount,
        discountApplied: responseData.discountApplied,
      };
    } catch (error) {
      console.error('Error creating payment intent:', error);
      return {
        success: false,
        error: error.message || 'Failed to create payment intent',
      };
    }
  }

  /**
   * Confirm payment with Stripe
   * @param {Object} params - Payment confirmation parameters
   * @param {string} params.clientSecret - Payment intent client secret
   * @param {Object} params.paymentMethod - Payment method details
   * @returns {Promise<Object>} Payment result
   */
  async confirmPayment({ clientSecret, paymentMethod }) {
    try {
      const stripe = await this.getStripe();

      const result = await stripe.confirmPayment({
        clientSecret,
        paymentMethod,
        redirect: 'if_required',
      });

      if (result.error) {
        return {
          success: false,
          error: result.error.message,
        };
      }

      return {
        success: true,
        paymentIntent: result.paymentIntent,
      };
    } catch (error) {
      console.error('Error confirming payment:', error);
      return {
        success: false,
        error: error.message || 'Failed to confirm payment',
      };
    }
  }

  /**
   * Verify payment status
   * @param {string} paymentIntentId - Payment intent ID
   * @param {string} userId - User ID
   * @returns {Promise<Object>} Payment status
   */
  async verifyPaymentStatus(paymentIntentId, userId) {
    try {
      const result = await verifyPaymentStatusFn({
        paymentIntentId,
        userId,
      });

      return {
        success: true,
        ...result.data,
      };
    } catch (error) {
      console.error('Error verifying payment status:', error);
      return {
        success: false,
        error: error.message || 'Failed to verify payment status',
      };
    }
  }

  /**
   * Get the display price for a plan, in euros.
   * Display only — the charged price is resolved server-side.
   * @param {string} planId - Plan ID
   * @returns {number} Price in euros, or 0 for unknown/retired plans
   */
  getPlanPrice(planId) {
    return PLAN_PRICES[planId] || 0;
  }

  /**
   * Format amount for display
   * @param {number} amount - Amount in euros
   * @returns {string} Formatted amount
   */
  formatAmount(amount) {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR',
    }).format(amount);
  }

  /**
   * Create a Stripe Subscription (for auto-renewal)
   * @param {Object} params - Subscription parameters
   * @param {number} params.amount - Amount in euros
   * @param {string} params.planId - Plan ID (e.g., 'monthly', 'quarterly')
   * @param {string} params.planName - Plan name for display
   * @param {string} params.userId - User ID
   * @param {string} params.currency - Currency code (default: 'eur')
   * @param {string} params.discountCode - Optional discount code
   * @param {string} params.paymentMethodId - Optional payment method ID
   * @returns {Promise<Object>} Subscription with client secret
   */
  async createSubscription({ amount, planId, planName, userId, currency = 'eur', discountCode, paymentMethodId }) {
    try {
      const result = await createSubscriptionFn({
        amount,
        planId,
        planName,
        userId,
        currency,
        discountCode: discountCode || null,
        paymentMethodId: paymentMethodId || null,
      });

      return {
        success: true,
        subscriptionId: result.data.subscriptionId,
        clientSecret: result.data.clientSecret,
        status: result.data.status,
        amount: result.data.amount,
        originalAmount: result.data.originalAmount,
        discountApplied: result.data.discountApplied,
      };
    } catch (error) {
      console.error('Error creating subscription:', error);
      return {
        success: false,
        error: error.message || 'Failed to create subscription',
      };
    }
  }

  /**
   * Get payment methods for current user
   * @param {string} userId - User ID
   * @returns {Promise<Object>} Payment methods list
   */
  async getPaymentMethods(userId) {
    try {
      const result = await getPaymentMethodsFn({ userId });
      return {
        success: true,
        paymentMethods: result.data.paymentMethods || [],
      };
    } catch (error) {
      console.error('Error getting payment methods:', error);
      return {
        success: false,
        error: error.message || 'Failed to get payment methods',
        paymentMethods: [],
      };
    }
  }

  /**
   * Create setup intent for adding payment method
   * @param {string} userId - User ID
   * @returns {Promise<Object>} Setup intent with client secret
   */
  async createSetupIntent(userId) {
    try {
      const result = await createSetupIntentFn({ userId });
      return {
        success: true,
        clientSecret: result.data.clientSecret,
      };
    } catch (error) {
      console.error('Error creating setup intent:', error);
      return {
        success: false,
        error: error.message || 'Failed to create setup intent',
      };
    }
  }

  /**
   * Set default payment method
   * @param {string} userId - User ID
   * @param {string} paymentMethodId - Payment method ID
   * @returns {Promise<Object>} Result
   */
  async setDefaultPaymentMethod(userId, paymentMethodId) {
    try {
      const result = await setDefaultPaymentMethodFn({ userId, paymentMethodId });
      return {
        success: true,
        ...result.data,
      };
    } catch (error) {
      console.error('Error setting default payment method:', error);
      return {
        success: false,
        error: error.message || 'Failed to set default payment method',
      };
    }
  }

  /**
   * Delete payment method
   * @param {string} userId - User ID
   * @param {string} paymentMethodId - Payment method ID
   * @returns {Promise<Object>} Result
   */
  async deletePaymentMethod(userId, paymentMethodId) {
    try {
      const result = await deletePaymentMethodFn({ userId, paymentMethodId });
      return {
        success: true,
        ...result.data,
      };
    } catch (error) {
      console.error('Error deleting payment method:', error);
      return {
        success: false,
        error: error.message || 'Failed to delete payment method',
      };
    }
  }
}

export default new StripeService();
